import { HIGHLIGHT_WORDS_FOR_QUESTION } from '@/constant';
import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { Button } from '@/routes/_embaUserLayout/-components/Button';
import { highlightText } from '@/utils/highlighter';
import { safeHtml } from '@/utils/safeHtml';
import classNames from 'classnames';
import type React from 'react';
import { Character } from '../../Character';
import { Dialog } from '../../Dialog';
import failed from './-assets/failed.svg';
import success from './-assets/success.svg';
import styles from './index.module.scss';

export const COURSE_LESSON_SCENARIO_STORY_QUIZ_FRAGMENT =
  graphql(/* GraphQL */ `
  fragment EmbaCourseLessonScenarioStoryQuiz on ScenarioContentQuiz {
    ...on ScenarioContentQuiz {
      dialog
      name
      character
      quiz {
        question  
        choices {
          slug
          text
          correct
        }
      }
    }
  }
`);

type QuizProps = {
  currentStoryContent: FragmentType<
    typeof COURSE_LESSON_SCENARIO_STORY_QUIZ_FRAGMENT
  >;
  isEnableNext: boolean;
  animationComplete: () => void;
  typingSpeed: number;
  showBlinkingArrow: boolean;
  slideToNext: () => void;
  embaUserlastName: string;
  showInteractions: boolean;
  setShowInteractions: (value: boolean) => void;
  setShowQuiz: (value: boolean) => void;
  showQuiz: boolean;
  isQuizCorrect: boolean | null;
  quizModalClose: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  setIsQuizCorrect: (value: boolean | null) => void;
  handleChoiceClick: (isCorrect: boolean) => void;
};

export const Quiz: React.FC<QuizProps> = ({
  currentStoryContent,
  isEnableNext,
  animationComplete,
  typingSpeed,
  showBlinkingArrow,
  slideToNext,
  embaUserlastName,
  showInteractions,
  setShowQuiz,
  showQuiz,
  isQuizCorrect,
  quizModalClose,
  setIsQuizCorrect,
  handleChoiceClick,
}) => {
  const currentStoryContentData = useFragment(
    COURSE_LESSON_SCENARIO_STORY_QUIZ_FRAGMENT,
    currentStoryContent,
  );

  return (
    <>
      <Character character={currentStoryContentData.character} />
      <div className={styles.contentArea}>
        <div
          className={classNames(styles.interactions, {
            [styles.display]: showInteractions,
          })}
        >
          <div className={styles.overlayContent}>
            <div
              className={classNames(styles.interactions, {
                [styles.display]: showInteractions,
              })}
            >
              <div className={styles.quiz}>
                <Button onClick={() => setShowQuiz(true)}>クイズを解く</Button>
                {showQuiz && (
                  <div
                    className={styles.quizModal}
                    onClick={() => quizModalClose}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={styles.quizModalInner}>
                      <div className={styles.quizBody}>
                        <div className={styles.quizTitle}>
                          <h3>ミニクイズ</h3>
                        </div>
                        {isQuizCorrect == null ? (
                          <div className={styles.quizContentArea}>
                            <p
                              className={styles.quizContent}
                              // biome-ignore lint/security/noDangerouslySetInnerHtml: サニタイズ済みのHTMLを表示するため
                              dangerouslySetInnerHTML={{
                                __html: safeHtml(
                                  highlightText(
                                    currentStoryContentData.quiz.question,
                                    HIGHLIGHT_WORDS_FOR_QUESTION,
                                  ),
                                ),
                              }}
                            />

                            <ul className={styles.quizChoisesList}>
                              {currentStoryContentData.quiz.choices.map(
                                (choice, index) => (
                                  <li key={choice.slug}>
                                    <span>{index + 1}</span>
                                    <p>{choice.text}</p>
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        ) : (
                          <div className={styles.quizResult}>
                            {isQuizCorrect ? (
                              <>
                                <p className={styles.quizResult__success}>
                                  正解です！
                                </p>
                                <div className={styles.quizResult__images}>
                                  <img src={success} alt="" />
                                  {/* TODO: 正誤キャラクター表示する */}
                                  {/* <img
                                          className={
                                            styles.quizResult__character
                                          }
                                          src="/character/saki_success.png"
                                          alt=""
                                        /> */}
                                </div>
                                <Button onClick={() => slideToNext()}>
                                  先に進む
                                </Button>
                              </>
                            ) : (
                              <>
                                <p className={styles.quizResult__failed}>
                                  不正解です！
                                </p>
                                <div className={styles.quizResult__images}>
                                  <img src={failed} alt="" />
                                  {/* TODO: 正誤キャラクター表示する */}
                                  {/* <img
                                          className={
                                            styles.quizResult__character
                                          }
                                          src="/character/saki_failed.png"
                                          alt=""
                                        /> */}
                                </div>
                                <Button onClick={() => setIsQuizCorrect(null)}>
                                  もう一度回答する
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {isQuizCorrect == null && (
                        <div className={styles.quizChoises}>
                          {currentStoryContentData.quiz.choices.map(
                            (choice, index) => (
                              <Button
                                key={choice.slug}
                                onClick={() =>
                                  handleChoiceClick(!!choice.correct)
                                }
                              >
                                {index + 1}
                              </Button>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        dialog={currentStoryContentData.dialog}
        name={currentStoryContentData.name}
        embaUserlastName={embaUserlastName}
        isEnableNext={isEnableNext}
        slideToNext={slideToNext}
        animationComplete={animationComplete}
        typingSpeed={typingSpeed}
        showBlinkingArrow={showBlinkingArrow}
      />
    </>
  );
};
